import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "../ui/sonner";
import { ICON_OPTIONS, IconFromKey } from "../../lib/iconMap";

/** Generic CRUD manager for one CMS section (`/admin/content/{section}`). Sections only differ
 * in which fields they collect — passing a `fields` config keeps this to one component instead
 * of seven near-identical ones for FAQs, Safety Points, About/Safety text blocks, Session Types,
 * Announcements, Photographer Benefits and Legal Docs. */
export const ContentItemsManager = ({ section, fields, itemLabel, forcedValues = {}, filterFn }) => {
  const [items, setItems] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/admin/content/${section}`).then(({ data }) => setItems(filterFn ? data.filter(filterFn) : data));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const startNew = () => {
    const base = fields.reduce((acc, f) => ({ ...acc, [f.key]: f.type === "switch" ? false : "" }), {});
    setForm({ ...base, ...forcedValues, order: items?.length || 0, published: true });
    setEditingId("new");
  };
  const startEdit = (item) => {
    setForm({ ...item });
    setEditingId(item.id);
  };
  const cancel = () => setEditingId(null);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, ...forcedValues };
      if (editingId === "new") {
        await api.post(`/admin/content/${section}`, payload);
        toast.success(`${itemLabel} created`);
      } else {
        await api.put(`/admin/content/${section}/${editingId}`, payload);
        toast.success(`${itemLabel} updated`);
      }
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/content/${section}/${id}`);
      toast.success(`${itemLabel} deleted`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete");
    }
  };

  const togglePublished = async (item) => {
    await api.put(`/admin/content/${section}/${item.id}`, { ...item, published: !item.published });
    load();
  };

  if (items === null) return <p className="font-medium text-midnight-black/60">Loading {itemLabel.toLowerCase()}s...</p>;

  return (
    <div data-testid={`content-manager-${section}`}>
      {editingId === null && (
        <div className="flex justify-end mb-4">
          <Button onClick={startNew} data-testid={`content-add-${section}`} className="bg-stranger-orange hover:bg-orange-600 text-clean-white rounded-full font-bold text-xs gap-2">
            <Plus className="w-3.5 h-3.5" /> ADD {itemLabel.toUpperCase()}
          </Button>
        </div>
      )}

      {editingId !== null && (
        <div className="border-2 border-stranger-orange rounded-xl p-5 mb-5 space-y-4" data-testid={`content-form-${section}`}>
          {fields.map((f) => (
            <div key={f.key}>
              <label className="text-xs font-bold uppercase tracking-wide block mb-1.5">{f.label}</label>
              {f.type === "textarea" ? (
                <Textarea data-testid={`content-field-${f.key}`} rows={4} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
              ) : f.type === "switch" ? (
                <Switch data-testid={`content-field-${f.key}`} checked={!!form[f.key]} onCheckedChange={(v) => setForm({ ...form, [f.key]: v })} />
              ) : f.type === "icon-select" ? (
                <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                  <SelectTrigger data-testid={`content-field-${f.key}`}>
                    <SelectValue placeholder="Choose icon" />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "date" ? (
                <Input data-testid={`content-field-${f.key}`} type="date" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
              ) : (
                <Input data-testid={`content-field-${f.key}`} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
              )}
            </div>
          ))}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide block mb-1.5">Order (lower shows first)</label>
            <Input data-testid="content-field-order" type="number" value={form.order ?? 0} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="w-32" />
          </div>
          <div className="flex items-center gap-3">
            <Switch data-testid="content-field-published" checked={form.published !== false} onCheckedChange={(v) => setForm({ ...form, published: v })} />
            <span className="text-xs font-bold uppercase tracking-wide">Published (visible on the site)</span>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Button onClick={save} disabled={saving} data-testid={`content-save-${section}`} className="bg-midnight-black hover:bg-black/80 text-clean-white rounded-full font-bold text-xs">
              {saving ? "SAVING..." : "SAVE"}
            </Button>
            <Button variant="outline" onClick={cancel} data-testid={`content-cancel-${section}`} className="rounded-full font-bold text-xs">
              CANCEL
            </Button>
          </div>
        </div>
      )}

      {items.length === 0 && editingId === null ? (
        <p className="font-medium text-midnight-black/60" data-testid={`content-empty-${section}`}>
          No {itemLabel.toLowerCase()}s yet.
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="border border-soft-gray rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`content-row-${item.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {item.icon_key && <IconFromKey iconKey={item.icon_key} className="w-4 h-4 text-stranger-orange" />}
                  <p className="font-extrabold text-sm">{item.title || item.slug || (item.body || "").slice(0, 60)}</p>
                  {!item.published && <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-soft-gray text-midnight-black/60">Unpublished</span>}
                  {section === "announcement" && item.active && <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-stranger-orange text-clean-white">Active</span>}
                </div>
                <p className="text-xs font-medium text-midnight-black/60 line-clamp-2">{item.body}</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => togglePublished(item)} data-testid={`content-toggle-published-${item.id}`} className="text-[10px] font-bold uppercase tracking-wide text-midnight-black/60 hover:text-midnight-black">
                  {item.published ? "UNPUBLISH" : "PUBLISH"}
                </button>
                <Button variant="outline" onClick={() => startEdit(item)} data-testid={`content-edit-${item.id}`} className="rounded-full font-bold text-xs">
                  EDIT
                </Button>
                <button onClick={() => remove(item.id)} data-testid={`content-delete-${item.id}`} className="text-midnight-black/50 hover:text-red-600" aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
